import { Flex, Modal, ScrollArea, Text } from "@mantine/core";
import { useEffect, useMemo, useRef } from "react";
import { DateTimeDisp } from "src/components/shared/DateTime";
import { usePostApi } from "src/hooks";
import { type IComment } from "src/pages/Summary/Summary.types";
import { useSelector } from "src/redux/hooks";

import CommentInput from "./CommentInput";
interface SharedProps {
  opened: boolean;
  onClose: () => void;
  comments: IComment[] | null | undefined;
}

interface EditableProps {
  lbeid?: string;
  partid?: string;
  meid?: string;
  weid?: string;
  setReviewComments: (
    comments: IComment[],
    lbeid?: string,
    partid?: string,
    meid?: string,
    weid?: string,
  ) => void;
}

type Props =
  | ({ type: "readonly" } & SharedProps)
  | ({ type: "editable" } & SharedProps & EditableProps);

const CommentsModal = ({ opened, onClose, comments, ...props }: Props) => {
  const isInitScroll = useRef(false);

  const formattedComments = useMemo(
    () => (typeof comments === "string" ? JSON.parse(comments) : comments),
    [comments],
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  const { post, isLoading } = usePostApi();
  const uppercase = useSelector(state => state.preferencesSlice.uppercase);

  useEffect(() => {
    setTimeout(() => {
      if (
        opened &&
        scrollRef.current &&
        !isInitScroll.current &&
        formattedComments &&
        formattedComments.length > 0
      ) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "instant",
        });
        isInitScroll.current = true;
      }
    }, 100);
  }, [formattedComments, opened]);

  const handleSend = async (comment: string) => {
    if (props.type === "editable") {
      const { lbeid, partid, meid, weid, setReviewComments } = props;
      await post({
        request: "send-chat",
        body: {
          params: {
            lbeid,
            partid,
            meid,
            weid,
            message: comment,
          },
        },
        onSuccess: res => {
          setReviewComments(res.comments, lbeid, partid, meid, weid);
          requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({
              top: scrollRef.current.scrollHeight,
              behavior: "smooth",
            });
          });
        },
      });
    }
  };

  const handleClose = () => {
    onClose();
    isInitScroll.current = false;
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Comments" size="40vw">
      <ScrollArea
        viewportRef={scrollRef}
        my={20}
        type="auto"
        h="40vh"
        offsetScrollbars>
        <Flex direction="column" gap={12} px={12}>
          {formattedComments?.map(comment => (
            <Flex
              key={`${comment.date}${comment.from}`}
              direction="column"
              p={10}
              bg="main.9"
              gap={8}
              maw="70%"
              style={{
                borderRadius: 12,
              }}>
              <Flex justify="space-between">
                <Text size="xs" c="white">
                  {comment.from}
                </Text>
                <DateTimeDisp
                  size="xs"
                  color="white"
                  utc={comment?.dateUTC}
                  lt={comment?.date}
                />
              </Flex>
              <Text c="white" style={{ borderRadius: 12 }}>
                {uppercase ? comment.message.toUpperCase() : comment.message}
              </Text>
            </Flex>
          ))}
        </Flex>
      </ScrollArea>
      {props.type === "editable" && (
        <CommentInput onSend={handleSend} isLoading={isLoading} />
      )}
    </Modal>
  );
};

export default CommentsModal;
